<script setup>
import { ref, computed } from "vue";
import FileUpload from "primevue/fileupload";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { useToast } from "primevue/usetoast";
import { dashboardAPI } from "../service/api.js";

// Bulk EOR viewer approvals (ClickUp 86bcrr1jw): choosing a tax_id,email CSV
// runs a preview first; nothing is written until the user clicks Import.
const toast = useToast();
const uploader = ref(null);
const visible = ref(false);
const file = ref(null);
const result = ref(null);
const loading = ref(false);

const isPreview = computed(() => result.value?.dryRun);
const header = computed(() => (isPreview.value ? "Preview: EOR email upload" : "EOR emails imported"));

const errorDetail = (error) =>
  (typeof error.response?.data === "string" && error.response.data) || error.message || "Upload failed";

const onSelect = async (event) => {
  file.value = event.files[0];
  uploader.value?.clear();
  loading.value = true;
  try {
    const { data } = await dashboardAPI.importApprovedEmails(file.value, true);
    result.value = data;
    visible.value = true;
  } catch (error) {
    console.error("EOR email preview failed:", error);
    toast.add({ severity: "error", summary: "Could not read file", detail: errorDetail(error), life: 8000 });
  } finally {
    loading.value = false;
  }
};

const runImport = async () => {
  loading.value = true;
  try {
    const { data } = await dashboardAPI.importApprovedEmails(file.value, false);
    result.value = data;
    toast.add({
      severity: "success",
      summary: "EOR emails imported",
      detail: `${data.approvalsAdded} approvals added across ${data.vendorsAffected} vendors.`,
      life: 5000,
    });
  } catch (error) {
    console.error("EOR email import failed:", error);
    toast.add({ severity: "error", summary: "Import failed", detail: errorDetail(error), life: 8000 });
  } finally {
    loading.value = false;
  }
};

// One CSV of everything that wasn't imported, to send back to whoever
// supplied the list.
const downloadReport = () => {
  const r = result.value;
  const rows = [["issue", "tax_id", "vendor", "email", "detail"]];
  for (const nf of r.notFound) for (const e of nf.emails) rows.push(["tax ID not found", nf.taxId, "", e, ""]);
  for (const rv of r.rejectedVendors)
    for (const e of rv.emails) rows.push(["vendor rejected", rv.taxId, rv.vendorName, e, rv.vendorId]);
  for (const ir of r.invalidRows) rows.push(["invalid row", ir.taxId, "", ir.email, `line ${ir.line}: ${ir.reason}`]);
  const csv = rows.map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\r\n");
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  link.download = "eor-email-import-report.csv";
  link.click();
  URL.revokeObjectURL(link.href);
};

const hasIssues = computed(
  () => result.value && (result.value.notFound.length || result.value.rejectedVendors.length || result.value.invalidRows.length)
);
</script>

<template>
  <FileUpload
    ref="uploader"
    mode="basic"
    name="file"
    accept=".csv,text/csv"
    customUpload
    :auto="true"
    :disabled="loading"
    chooseLabel="Upload EOR Emails"
    @select="onSelect"
  />
  <Dialog v-model:visible="visible" :header="header" modal style="width: 52rem">
    <template v-if="result">
      <p v-if="isPreview" class="note">
        Nothing has been saved yet. Each email is approved for every vendor with its tax ID; existing
        approvals are kept, and rejected vendors are skipped.
      </p>
      <dl class="summary">
        <dt>Rows read</dt><dd>{{ result.rowsRead }}</dd>
        <dt>{{ isPreview ? "Approvals to add" : "Approvals added" }}</dt><dd>{{ result.approvalsAdded }}</dd>
        <dt>Already approved</dt><dd>{{ result.alreadyApproved }}</dd>
        <dt>Vendors affected</dt><dd>{{ result.vendorsAffected }}</dd>
        <dt>Tax IDs matched</dt><dd>{{ result.taxIdsMatched }}</dd>
        <dt>Duplicate rows</dt><dd>{{ result.duplicateRows }}</dd>
      </dl>

      <template v-if="result.notFound.length">
        <h4>Tax IDs not found in CarePay ({{ result.notFound.length }})</h4>
        <DataTable :value="result.notFound" size="small" scrollable scrollHeight="12rem">
          <Column field="taxId" header="Tax ID" style="width: 9rem" />
          <Column header="Emails">
            <template #body="{ data }">{{ data.emails.join(", ") }}</template>
          </Column>
        </DataTable>
      </template>

      <template v-if="result.rejectedVendors.length">
        <h4>Rejected vendors skipped ({{ result.rejectedVendors.length }})</h4>
        <DataTable :value="result.rejectedVendors" size="small" scrollable scrollHeight="12rem">
          <Column field="taxId" header="Tax ID" style="width: 9rem" />
          <Column field="vendorName" header="Vendor" />
          <Column header="Emails">
            <template #body="{ data }">{{ data.emails.join(", ") }}</template>
          </Column>
        </DataTable>
      </template>

      <template v-if="result.invalidRows.length">
        <h4>Invalid rows ({{ result.invalidRows.length }})</h4>
        <DataTable :value="result.invalidRows" size="small" scrollable scrollHeight="12rem">
          <Column field="line" header="Line" style="width: 5rem" />
          <Column field="taxId" header="Tax ID" style="width: 9rem" />
          <Column field="email" header="Email" />
          <Column field="reason" header="Reason" />
        </DataTable>
      </template>
    </template>

    <template #footer>
      <Button v-if="hasIssues" label="Download report" icon="pi pi-download" severity="secondary" text @click="downloadReport" />
      <template v-if="isPreview">
        <Button label="Cancel" severity="secondary" outlined @click="visible = false" />
        <Button
          :label="`Import ${result?.approvalsAdded ?? 0} approvals`"
          :disabled="!result?.approvalsAdded"
          :loading="loading"
          @click="runImport"
        />
      </template>
      <Button v-else label="Close" @click="visible = false" />
    </template>
  </Dialog>
</template>

<style scoped>
.note {
  margin-top: 0;
}
.summary {
  display: grid;
  grid-template-columns: max-content auto;
  gap: 0.25rem 1.5rem;
  margin: 0 0 1rem;
}
.summary dt {
  font-weight: 600;
}
.summary dd {
  margin: 0;
}
h4 {
  margin: 1rem 0 0.5rem;
}
</style>
